import type { RunView, StepView } from "../api/types";
import { grouped } from "./format";

/**
 * What a run that stopped before it had done everything says about it, or `null` for one that did not.
 *
 * The pages it never reached are counted apart from the ones it looked at, so they are said with the
 * reason: a run that stopped with nothing left in hand reads differently from one that left thousands.
 * A run the wiki stopped has already failed with the same words, so they are not said a second time.
 */
export function stoppedEarly(
  run: Pick<RunView, "stopReason" | "notAttempted" | "error">,
): string | null {
  const left =
    run.notAttempted > 0 ? `${grouped(run.notAttempted)} not attempted` : null;

  if (!run.stopReason || run.stopReason === run.error) {
    return left === null ? null : `Stopped early: ${left}.`;
  }

  return left === null
    ? `Stopped early: ${run.stopReason}.`
    : `Stopped early: ${run.stopReason}; ${left}.`;
}

/**
 * Whether a step's stop reason is worth printing under it.
 *
 * A stop is recorded on the step it happened in, on every step around it and on the run, all with the
 * same words. It is printed once: on the run, or, where a step stopped for a reason the run does not
 * give, on the innermost step that gives it.
 *
 * @param step the step in question.
 * @param steps every step of the run, to find the ones inside [step].
 * @param said what the run already says about why it stopped, if anything.
 */
export function stopIsNew(
  step: StepView,
  steps: StepView[],
  said: string | null,
): boolean {
  if (!step.stopReason || step.stopReason === said) return false;
  return !steps.some(
    (inner) => inner.parent === step.id && inner.stopReason === step.stopReason,
  );
}
